import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from './ui/dialog';
import { 
  HandHelping, 
  Plus, 
  Clock, 
  CheckCircle2, 
  UserCheck, 
  Loader2, 
  AlertCircle,
  CalendarDays,
  Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CATEGORIES = ['Scoreboard', 'Snacks', 'Field Prep', 'Concessions', 'Parking', 'Photography', 'Other'];

const STATUS_BADGES = {
  open: { label: 'Open', className: 'bg-green-600' },
  claimed: { label: 'Filled', className: 'bg-blue-600' },
  completed: { label: 'Done', className: 'bg-slate-600' },
  cancelled: { label: 'Cancelled', className: 'bg-red-600' },
};

export default function VolunteerPanel({ orgId, teamId, canManage = false }) {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('available');
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'Scoreboard',
    event_date: '',
    start_time: '',
    duration_minutes: 60,
    slots_available: 1,
    team_id: teamId || '',
  });

  const fetchTasks = useCallback(async () => {
    if (!orgId) return;
    try {
      const params = new URLSearchParams({ upcoming_only: 'true' });
      if (teamId) params.append('team_id', teamId);
      
      const [tasksRes, myTasksRes, orgsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tasks/org/${orgId}?${params}`),
        axios.get(`${API_URL}/api/tasks/user/my`),
        axios.get(`${API_URL}/api/organizations`),
      ]);
      
      setTasks(tasksRes.data || []);
      setMyTasks(myTasksRes.data || []);
      const org = (orgsRes.data || []).find(o => o.id === orgId);
      if (org?.settings?.volunteer_categories?.length) setCategories(org.settings.volunteer_categories);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId, teamId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setError('');
    
    try {
      await axios.post(`${API_URL}/api/tasks/org/${orgId}`, {
        ...form,
        event_date: new Date(form.event_date).toISOString(),
        duration_minutes: parseInt(form.duration_minutes),
        slots_available: parseInt(form.slots_available),
      });
      
      setShowCreate(false);
      setForm({ name: '', description: '', category: 'Scoreboard', event_date: '', start_time: '', duration_minutes: 60, slots_available: 1, team_id: teamId || '' });
      fetchTasks();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create task');
    } finally {
      setCreateLoading(false);
    }
  };

  const claimTask = async (taskId) => {
    setClaiming(taskId);
    try {
      await axios.post(`${API_URL}/api/tasks/${taskId}/claim`);
      fetchTasks();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to claim task');
    } finally {
      setClaiming(null);
    }
  };

  const unclaimTask = async (taskId) => {
    setClaiming(taskId);
    try {
      await axios.post(`${API_URL}/api/tasks/${taskId}/unclaim`);
      fetchTasks();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to unclaim task');
    } finally {
      setClaiming(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  const myTaskIds = new Set(myTasks.map(t => t.id));
  const availableTasks = tasks.filter(t => t.can_claim && !myTaskIds.has(t.id));
  const displayTasks = activeView === 'available' ? availableTasks : myTasks;

  return (
    <div className="space-y-4">
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className={`flex items-center gap-2 text-lg ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <HandHelping className="h-5 w-5 text-amber-500" />
            Volunteer Tasks
          </CardTitle>
          {canManage && (
            <Button 
              size="sm" 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => setShowCreate(true)}
              data-testid="create-task-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Create Task
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {/* View Toggle */}
          <div className={`flex gap-1 p-1 rounded-lg mb-4 ${isDark ? 'bg-slate-700/50' : 'bg-slate-100'}`}>
            <button
              onClick={() => setActiveView('available')}
              className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                activeView === 'available'
                  ? 'bg-amber-600 text-white'
                  : isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="view-available-tasks"
            >
              Available ({availableTasks.length})
            </button>
            <button
              onClick={() => setActiveView('mine')}
              className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                activeView === 'mine'
                  ? 'bg-amber-600 text-white'
                  : isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid="view-my-tasks"
            >
              My Tasks ({myTasks.length})
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 flex items-center gap-2 text-red-400 mb-4">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {displayTasks.length === 0 ? (
            <div className={`text-center py-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <HandHelping className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>{activeView === 'available' ? 'No tasks available right now' : 'You haven\'t signed up for any tasks'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {displayTasks.map((task) => {
                const statusBadge = STATUS_BADGES[task.status] || STATUS_BADGES.open;
                const isMine = myTaskIds.has(task.id);
                
                return (
                  <div 
                    key={task.id}
                    className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                    data-testid={`task-${task.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{task.name}</h4>
                        {task.description && (
                          <p className={`text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{task.description}</p>
                        )}
                      </div>
                      <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
                    </div>
                    
                    <div className={`flex flex-wrap gap-3 text-xs mb-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      <span className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        {task.category}
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />
                        {task.event_date ? new Date(task.event_date).toLocaleDateString() : 'TBD'}
                      </span>
                      {task.start_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {task.start_time}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <UserCheck className="h-3 w-3" />
                        {task.slots_filled || 0}/{task.slots_available} filled
                      </span>
                    </div>

                    {/* Assigned volunteers */}
                    {task.assigned_to?.length > 0 && (
                      <div className={`mb-3 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Signed up: {task.assigned_to.map(a => a.name).join(', ')}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex justify-end">
                      {isMine ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => unclaimTask(task.id)}
                          disabled={claiming === task.id}
                          className={isDark ? 'border-slate-600 text-slate-300' : ''}
                          data-testid={`unclaim-task-${task.id}`}
                        >
                          {claiming === task.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Withdraw'}
                        </Button>
                      ) : task.can_claim ? (
                        <Button
                          size="sm"
                          onClick={() => claimTask(task.id)}
                          disabled={claiming === task.id}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`claim-task-${task.id}`}
                        >
                          {claiming === task.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Sign Up'}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Task Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-slate-900'}>Create Volunteer Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Task Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="e.g. Scoreboard Operator"
                required
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                data-testid="task-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                placeholder="Brief description of duties"
                className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                data-testid="task-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Category *</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({...form, category: e.target.value})}
                  className={`w-full h-10 rounded-md px-3 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="task-category-select"
                >
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Event Date *</Label>
                <Input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({...form, event_date: e.target.value})}
                  required
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                  data-testid="task-date-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Start Time</Label>
                <Input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({...form, start_time: e.target.value})}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                  data-testid="task-time-input"
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Duration (min)</Label>
                <Input
                  type="number"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({...form, duration_minutes: e.target.value})}
                  min="15"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                  data-testid="task-duration-input"
                />
              </div>
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Slots *</Label>
                <Input
                  type="number"
                  value={form.slots_available}
                  onChange={(e) => setForm({...form, slots_available: e.target.value})}
                  min="1"
                  required
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''}
                  data-testid="task-slots-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={createLoading} className="bg-amber-600 hover:bg-amber-700" data-testid="task-submit-btn">
                {createLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Task'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
